import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { marked } from 'marked';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import MinimizeIcon from '@material-ui/icons/Minimize';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import AddCommentOutlinedIcon from '@material-ui/icons/AddCommentOutlined';
import SendIcon from '@material-ui/icons/Send';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CloseIcon from '@material-ui/icons/Close';
import StopIcon from '@material-ui/icons/Stop';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import LightbulbOutlinedIcon from '@material-ui/icons/EmojiObjectsOutlined';
import toast from 'react-hot-toast';
import { fetchApi } from '../../utils/fetchHelper';
import endpoints from '../../configs/endpoints';
import config from '../../configs';
import { getAccessToken } from '../../services/authService';
import { getPinnedProjects, unpinProject, ACTIVE_PROJECT_EVENT } from '../../utils/activeProject';
import { useRegiPageContext } from '../../utils/regiContext';

const HIDDEN_PATHS = ['/login', '/sign-up', '/register', '/forgot-password', '/reset-password', '/verify-email', '/session/login', '/session/'];
const ACCEPT_EXTS = /\.(xlsx|xls|csv|docx|doc|txt|pdf)$/i;
const IMAGE_EXTS = /\.(png|jpe?g|gif|webp)$/i;
const ACCEPT_ANY = /\.(xlsx|xls|csv|docx|doc|txt|pdf|png|jpe?g|gif|webp)$/i;

// Sanitize AI-generated HTML: allow only safe tags, strip all attributes except
// safe href on <a>. Mirrors AiChatDialog.sanitizeAiHtml to prevent XSS from
// stored chat history that might contain injected markup.
const ALLOWED_AI_TAGS = new Set(['P', 'BR', 'STRONG', 'EM', 'B', 'I', 'U', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'BLOCKQUOTE', 'A', 'SPAN', 'DIV', 'PRE', 'CODE', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD']);
const sanitizeAiHtml = (raw) => {
    if (typeof raw !== 'string' || !raw) return '';
    const doc = new DOMParser().parseFromString(`<div>${raw}</div>`, 'text/html');
    const root = doc.body && doc.body.firstChild;
    if (!root) return '';
    const walk = (node) => {
        const children = Array.from(node.childNodes);
        for (const child of children) {
            if (child.nodeType === 1) {
                if (!ALLOWED_AI_TAGS.has(child.tagName)) {
                    child.replaceWith(doc.createTextNode(child.textContent || ''));
                    continue;
                }
                Array.from(child.attributes).forEach((attr) => {
                    const lower = attr.name.toLowerCase();
                    const val = String(attr.value || '').trim();
                    const isSafeHref =
                        child.tagName === 'A' && lower === 'href' &&
                        (/^(https?:|mailto:)/i.test(val) || /^\/api\/(exports|uploads|attachments)\//i.test(val));
                    if (!isSafeHref) child.removeAttribute(attr.name);
                });
                if (child.tagName === 'A') {
                    const href = child.getAttribute('href') || '';
                    if (/^\/api\/(exports|uploads|attachments)\//i.test(href)) {
                        const backendBase = (config[config.env].httpURL || '').replace(/\/api\/v1\/?$/, '').replace(/\/+$/, '');
                        child.setAttribute('href', `${backendBase}${href}`);
                        // Files are stored under a UUID name; get_document_link passes the real
                        // filename as ?name=, so save under that instead of the UUID.
                        let downloadName = href.split('?')[0].split('/').pop();
                        try {
                            const nameParam = new URL(href, 'http://x').searchParams.get('name');
                            if (nameParam) downloadName = nameParam;
                        } catch (e) { /* malformed URL — keep the path-derived name */ }
                        child.setAttribute('download', downloadName);
                    }
                    child.setAttribute('target', '_blank');
                    child.setAttribute('rel', 'noopener noreferrer');
                }
                walk(child);
            } else if (child.nodeType !== 3 && child.nodeType !== 8) {
                child.remove();
            }
        }
    };
    walk(root);
    return root.innerHTML;
};
// Tools that mutate supplier/survey state → trigger Suppliers page refresh.
const REFRESH_TOOLS = new Set([
    'execute_pending_import', 'create_supplier', 'update_supplier', 'patch_suppliers',
    'delete_suppliers', 'delete_all_project_suppliers',
    'create_survey_from_pending', 'split_and_create_surveys', 'assign_survey_to_all_project_suppliers',
    'generate_personalized_surveys', 'update_survey', 'delete_surveys',
    // Was missing — a chat-driven email send never refreshed the Suppliers page, so status
    // chips (e.g. "No Reply") looked stale until a manual reload.
    'send_supplier_email',
    // Also missing: survey edits and attachments (the survey page kept the old content), and
    // run_server_code, which can change anything.
    'patch_surveys', 'add_survey_attachment', 'remove_survey_attachment', 'attach_chat_file_to_survey',
    'run_server_code',
]);
// Replies only change supplier status and feedback, which only the Suppliers page and the
// project overview show — both refresh in place on the event, so no full-page reload.
const EVENT_ONLY_TOOLS = new Set(['send_custom_email', 'execute_reply_batch']);
// Tools that create or delete projects → re-validate pinned sidebar entries.
const PROJECT_TOOLS = new Set(['create_project', 'delete_project']);

// ─── Draggable widget ────────────────────────────────────────────────────────
const DRAG_THRESHOLD = 5;
const clampToViewport = (pos, W = 56, H = 56) => {
    if (!pos) return null;
    return {
        left: Math.max(0, Math.min(Math.max(0, window.innerWidth - W), pos.left)),
        top: Math.max(0, Math.min(Math.max(0, window.innerHeight - H), pos.top)),
    };
};
const useDraggable = (storageKey, size = { width: 56, height: 56 }) => {
    const [pos, setPos] = React.useState(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (!raw) return null;
            const obj = JSON.parse(raw);
            if (typeof obj?.left === 'number' && typeof obj?.top === 'number') return clampToViewport(obj, size.width, size.height);
        } catch (e) { /* ignore */ }
        return null;
    });
    const posRef = React.useRef(pos);
    posRef.current = pos;
    const justDraggedRef = React.useRef(false);
    const startDrag = React.useCallback((e, getRect) => {
        if (e.button !== undefined && e.button !== 0) return;
        const sx = e.clientX, sy = e.clientY;
        const rect = getRect(); if (!rect) return;
        const sl = rect.left, st = rect.top, W = rect.width, H = rect.height;
        let dragged = false;
        const onMove = (ev) => {
            const dx = ev.clientX - sx, dy = ev.clientY - sy;
            if (!dragged && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
            dragged = true;
            const next = {
                left: Math.max(0, Math.min(Math.max(0, window.innerWidth - W), sl + dx)),
                top: Math.max(0, Math.min(Math.max(0, window.innerHeight - H), st + dy)),
            };
            posRef.current = next; setPos(next);
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            if (dragged) {
                justDraggedRef.current = true;
                setTimeout(() => { justDraggedRef.current = false; }, 100);
                try { localStorage.setItem(storageKey, JSON.stringify(posRef.current)); } catch (_) { /* ignore */ }
            }
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
    }, [storageKey]);
    const wasDragged = React.useCallback(() => justDraggedRef.current, []);
    React.useEffect(() => {
        const onResize = () => setPos((p) => p ? clampToViewport(p, size.width, size.height) : p);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [size.width, size.height]);
    return { pos, startDrag, wasDragged };
};

// ─── Suggestions ─────────────────────────────────────────────────────────────
const SUGGESTIONS = [
    { text: 'Help me import suppliers from a spreadsheet.' },
    { text: 'Which of my files are still missing AI Parse results?' },
    { text: 'Summarize REACH compliance status across my parsed files.' },
    { text: 'Explain Prop 65 in plain English.' },
    { text: 'List my suppliers without a survey assigned.' },
];

// ─── /chat/message SSE (2026-10-10) ─────────────────────────────────────────
// A multi-round agent turn can legitimately run minutes — the backend now streams heartbeats
// instead of one bare JSON response, so the old single-request timeout no longer applies. This
// does the opposite side of that: read the chunked SSE body and pull out the one `done` (or
// `error`) event it ends with. Heartbeat comment lines (`: keepalive`) are just skipped;
// `progress` events (which tools Regi is running right now) are handed to onProgress as they arrive.
const parseSseBody = async (body, onProgress) => {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let donePayload = null;
    let errorPayload = null;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const rawEvent = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            if (!rawEvent || rawEvent.startsWith(':')) continue; // heartbeat comment
            let eventType = 'message';
            let dataStr = '';
            rawEvent.split('\n').forEach((line) => {
                if (line.startsWith('event:')) eventType = line.slice(6).trim();
                else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
            });
            if (!dataStr) continue;
            try {
                const parsed = JSON.parse(dataStr);
                if (eventType === 'done') donePayload = parsed;
                else if (eventType === 'error') errorPayload = parsed;
                else if (eventType === 'progress' && onProgress) onProgress(parsed);
            } catch (e) { /* malformed event — ignore and keep reading */ }
        }
    }
    return { donePayload, errorPayload };
};

// Fallback for a stream that ends with neither a done nor an error event (dropped connection,
// proxy hiccup) — the backend never cancels a turn just because the client went away, so the
// reply is very often already saved by the time this runs. The saved reply carries the turn's
// write tools (writeTools in chat history), so the page refresh still happens on this path.
// Takes the FIRST reply saved after sentAtMs, not the last: after a Stop the user can keep
// chatting, and turns are serialized server-side, so this turn's reply is the earliest one.
const pollForSavedReply = async (sentAtMs, { attempts = 10, intervalMs = 3000 } = {}) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        try {
            const hist = await fetchApi({ method: 'GET', url: '/chat/history' });
            const msgs = Array.isArray(hist && hist.messages) ? hist.messages : [];
            const match = msgs.find((m) => m.role === 'assistant' && new Date(m.createdAt).getTime() > sentAtMs);
            if (match) return { reply: match.content, writeToolsExecuted: match.writeTools || [] };
        } catch (e) { /* keep trying */ }
    }
    return null;
};

// Shown next to the "thinking" dots while a turn runs, from the backend's `progress` events.
const TOOL_PROGRESS_LABELS = {
    list_suppliers: 'Looking up suppliers',
    list_projects: 'Looking up projects',
    list_emails: 'Checking emails',
    get_email_content: 'Reading the email',
    read_email_attachment: 'Reading the attachment',
    get_document_link: 'Getting the download link',
    draft_feedback_reply: 'Drafting the reply',
    stage_custom_email: 'Preparing the email',
    send_custom_email: 'Preparing the email',
    send_supplier_email: 'Sending the survey',
    list_delivery_failures: 'Checking delivery failures',
    list_files: 'Looking up files',
    read_file_content: 'Reading the file',
    get_parse_results: 'Checking parsed data',
    search_parse_results: 'Checking parsed data',
    search_raw_materials: 'Searching raw materials',
    list_surveys: 'Looking up surveys',
    get_survey: 'Looking up surveys',
    get_pending_import: 'Reading the uploaded file',
    get_pending_import_template: 'Reading the uploaded file',
    reload_import_files: 'Reading the uploaded file',
    execute_pending_import: 'Importing data',
    get_pending_reply_batch: 'Reviewing the reply batch',
    execute_reply_batch: 'Sending replies',
    generate_analytics_report: 'Building the report',
    fill_spreadsheet: 'Filling the spreadsheet',
    web_search: 'Searching the web',
};
const progressLabel = (tools) => {
    if (!tools || !tools.length) return '';
    const label = (t) => TOOL_PROGRESS_LABELS[t]
        || (/survey/.test(t) ? 'Updating surveys' : /supplier/.test(t) ? 'Updating suppliers' : /project/.test(t) ? 'Updating projects' : 'Working');
    return `${[...new Set(tools.map(label))].join(' · ')}…`;
};

const STOP_NOTE_CHECKING = 'Stopped — checking whether this message reached Regi…';
const STOP_NOTE_PROCESSING = 'Stopped waiting, but this message had already reached Regi — it is still being processed and the reply will appear here.';
const STOP_NOTE_STILL_WORKING = 'Regi is still working on this in the background — reopen Regi in a moment to see the reply.';
const NOTE_CONNECTION_DROPPED = 'The connection dropped, but this message had already reached Regi — it is still being processed and the reply will appear here.';

// Confirmation card for a staged custom email. Rendered from the backend's `stagedDraft` (the exact
// final email, built by the same code that sends it) — never from the model's chat text — and its
// buttons act on that exact draft via /chat/staged-draft.
const DRAFT_STATUS_TEXT = {
    sending: '处理中…',
    sent: '✓ 已发送',
    cancelled: '已取消，未发送',
    expired: '已失效（之后又发了消息或已超时），未发送',
    superseded: '已被新的草稿替代，未发送',
};
const DraftCard = ({ card, onAct, busy }) => {
    const frameRef = React.useRef(null);
    const fitFrame = () => {
        try {
            const doc = frameRef.current.contentDocument;
            frameRef.current.style.height = `${Math.min(doc.documentElement.scrollHeight + 4, 420)}px`;
        } catch (e) { /* keep the default height */ }
    };
    const field = (label, value) => (value ? (
        <div style={{ display: 'flex', gap: 6, fontSize: 12, lineHeight: 1.5 }}>
            <span style={{ color: '#8895a2', minWidth: 40, flexShrink: 0 }}>{label}</span>
            <span style={{ color: '#1a2733', wordBreak: 'break-all' }}>{value}</span>
        </div>
    ) : null);
    const pending = card.status === 'pending';
    const btn = (primary) => ({
        border: primary ? 'none' : '1px solid #c9d6e3', borderRadius: 6, padding: '5px 14px', fontSize: 12,
        cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
        background: primary ? '#4a90e2' : '#fff', color: primary ? '#fff' : '#1a2733',
    });
    return (
        <div style={{
            alignSelf: 'stretch', background: '#fff', border: '1px solid #c9d6e3', borderRadius: 10,
            padding: '10px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1a2733', marginBottom: 6 }}>待确认发送</div>
            {field('发件人', card.from)}
            {field('收件人', card.to)}
            {field('抄送', card.cc)}
            {field('主题', card.subject)}
            {card.attachments && card.attachments.length > 0 && field('附件', card.attachments.join(', '))}
            {card.warning && <div style={{ color: '#b45309', fontSize: 12, marginTop: 6 }}>⚠ {card.warning}</div>}
            {card.html && (
                <iframe
                    ref={frameRef}
                    title="Email preview"
                    sandbox="allow-same-origin"
                    srcDoc={card.html}
                    onLoad={fitFrame}
                    style={{ width: '100%', height: 240, marginTop: 8, border: '1px solid #e3e8ee', borderRadius: 6, background: '#fff' }}
                />
            )}
            {(card.error || card.statusText) && (
                <div style={{ color: card.status === 'sent' ? '#2e7d32' : '#b42318', fontSize: 12, marginTop: 6 }}>
                    {card.error || card.statusText}
                </div>
            )}
            {pending ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    {!card.error && (
                        <button type="button" disabled={busy} style={btn(true)} onClick={() => onAct(card.draftId, 'send')}>是，发送</button>
                    )}
                    <button type="button" disabled={busy} style={btn(false)} onClick={() => onAct(card.draftId, 'cancel')}>否</button>
                </div>
            ) : (
                <div style={{ marginTop: 8, fontSize: 12, color: '#5a6b7a' }}>{DRAFT_STATUS_TEXT[card.status] || ''}</div>
            )}
        </div>
    );
};

// ─── Component ───────────────────────────────────────────────────────────────
const Regi = () => {
    const userId = useSelector((s) => s.authReducer?.user?.id);
    const location = useLocation();
    const history = useHistory();
    const { context: regiPageContext } = useRegiPageContext();

    const [open, setOpen] = React.useState(false);
    const [messages, setMessages] = React.useState([]);
    const [input, setInput] = React.useState('');
    const [sending, setSending] = React.useState(false);
    const [progressTools, setProgressTools] = React.useState([]);

    // Confirmation cards (DraftCard) — kept in sync with the backend's single pending draft.
    const updateDraftCard = (draftId, patch) => setMessages((prev) => prev.map((m) => (
        m._draftCard && m._draftCard.draftId === draftId ? { ...m, _draftCard: { ...m._draftCard, ...patch } } : m
    )));
    const expireDraftCard = (draftId) => setMessages((prev) => prev.map((m) => (
        m._draftCard && m._draftCard.draftId === draftId && m._draftCard.status === 'pending'
            ? { ...m, _draftCard: { ...m._draftCard, status: 'expired' } } : m
    )));
    const addDraftCard = (draft) => {
        setMessages((prev) => {
            if (prev.some((m) => m._draftCard && m._draftCard.draftId === draft.draftId)) return prev; // already shown
            return [
                ...prev.map((m) => (m._draftCard && m._draftCard.status === 'pending'
                    ? { ...m, _draftCard: { ...m._draftCard, status: 'superseded' } } : m)),
                { role: 'assistant', _draftCard: { ...draft, status: 'pending' }, createdAt: new Date().toISOString() },
            ];
        });
        // The server drops a staged draft after a few minutes — show the card as expired then,
        // instead of a send button that would only fail with "no longer pending".
        if (typeof draft.expiresInMs === 'number') setTimeout(() => expireDraftCard(draft.draftId), draft.expiresInMs);
    };
    // Ask the backend for the current pending draft and show its card (used whenever a turn's
    // result arrived without draft info: after a reload, a dropped stream, or a Stop).
    const restoreDraftCard = () => fetchApi({ method: 'GET', url: '/chat/staged-draft' })
        .then((r) => { if (r && r.stagedDraft) addDraftCard(r.stagedDraft); })
        .catch(() => {});
    // Any card no longer matching the backend's pending draft can't be confirmed any more.
    const retireDraftCards = (pendingDraftId, sentViaText) => setMessages((prev) => prev.map((m) => (
        m._draftCard && m._draftCard.status === 'pending' && m._draftCard.draftId !== pendingDraftId
            ? { ...m, _draftCard: { ...m._draftCard, status: sentViaText ? 'sent' : 'expired' } } : m
    )));
    // After Regi changed data (a chat turn's write tools, or a confirmation card's send), make the
    // open page show it: the Suppliers page and project overview refresh in place on the event;
    // other pages reload for tools in REFRESH_TOOLS. Project create/delete also re-validates the
    // sidebar's pinned projects.
    const refreshAfterWrites = async (executed) => {
        const tools = Array.isArray(executed) ? executed : [];
        const needsReload = tools.some((t) => REFRESH_TOOLS.has(t));
        if (needsReload || tools.some((t) => EVENT_ONLY_TOOLS.has(t))) {
            window.dispatchEvent(new CustomEvent('suppliers-imported'));
        }
        if (needsReload) {
            // If the Suppliers page isn't mounted (user is on Dashboard/Projects/etc.),
            // force a full page reload so the new data is immediately visible.
            const onSuppliersPage = /\/project\/.+\/(suppliers|dashboard)/i.test(window.location.pathname)
                || window.location.pathname.includes('/suppliers');
            if (!onSuppliersPage) {
                setTimeout(() => window.location.reload(), 800);
            }
        }
        // Sync sidebar when projects were created or deleted.
        if (tools.some((t) => PROJECT_TOOLS.has(t))) {
            try {
                const projectsRes = await fetchApi({ method: 'GET', url: endpoints.projects });
                const liveIds = new Set((Array.isArray(projectsRes) ? projectsRes : []).map((p) => String(p._id || p.id)));
                const pinned = getPinnedProjects(userId);
                pinned.forEach((p) => { if (!liveIds.has(String(p.id))) unpinProject(userId, p.id); });
                window.dispatchEvent(new Event(ACTIVE_PROJECT_EVENT));
                // If currently viewing a deleted project, navigate to dashboard.
                const match = window.location.pathname.match(/\/project\/([a-f0-9]{24})/i);
                if (match && !liveIds.has(match[1])) {
                    history.push('/dashboard');
                }
            } catch (_) { /* sidebar sync is best-effort */ }
        }
    };
    const actOnDraft = async (draftId, action) => {
        updateDraftCard(draftId, { status: 'sending', statusText: '' });
        try {
            const res = await fetchApi({ method: 'POST', url: '/chat/staged-draft', data: { draftId, action } });
            if (action === 'cancel') updateDraftCard(draftId, { status: 'cancelled', statusText: '' });
            else updateDraftCard(draftId, res.sent ? { status: 'sent', statusText: res.reply } : { status: 'pending', statusText: res.reply });
            if (res && res.sent) refreshAfterWrites(['send_custom_email']);
            if (res && res.reply) {
                setMessages((prev) => [...prev, { role: 'assistant', content: res.reply, createdAt: new Date().toISOString() }]);
            }
        } catch (err) {
            updateDraftCard(draftId, {
                status: err && err.code === 409 ? 'expired' : 'pending',
                statusText: (err && (err.message || err.msg)) || 'Something went wrong.',
            });
        }
    };
    const [loadingHistory, setLoadingHistory] = React.useState(false);
    const [showSuggestions, setShowSuggestions] = React.useState(false);
    const [attachedFiles, setAttachedFiles] = React.useState([]);
    const [isDragging, setIsDragging] = React.useState(false);

    const fileInputRef = React.useRef(null);
    const scrollRef = React.useRef(null);
    const bubbleRef = React.useRef(null);
    const windowRef = React.useRef(null);
    const sendAbortRef = React.useRef(null);
    const bubbleDrag = useDraggable('regi-bubble-pos');
    // Chat window is 560x600 (see width/height below) — must clamp its restore-on-mount and
    // resize-reclamp against its own size, not the 56x56 default sized for the floating bubble
    // (only live-dragging read the real element size before), which left the window mostly
    // off-screen after a viewport resize.
    const windowDrag = useDraggable('regi-window-pos', { width: 560, height: 600 });

    const hidden = !userId || HIDDEN_PATHS.some((p) => location.pathname.toLowerCase().startsWith(p));

    const currentProjectId = React.useMemo(() => {
        const m = location.pathname.match(/^\/project\/([a-f0-9]{24})(\/|$)/i);
        return m ? m[1] : null;
    }, [location.pathname]);

    // Load history on first open
    const loadedRef = React.useRef(false);
    // Bumped exactly once, from inside the history fetch's own .finally(), the instant history has
    // actually finished loading. The Phase-2 effect below depends on THIS instead of loadingHistory
    // — two effects scheduled by the same render both close over that render's (pre-update) state,
    // so on the very first open the Phase-2 effect saw loadingHistory's stale pre-fetch value
    // (false) and fired concurrently with the history fetch instead of after it; whichever request
    // landed second then clobbered the other's setMessages, yet the alert was already ack'd either
    // way, permanently losing it. A value only ever written inside the resolved promise's own
    // callback can't be read stale like that — it simply doesn't exist until history is truly done.
    const [historyReadyTick, setHistoryReadyTick] = React.useState(0);
    // Load chat history once per page load. Phase 1 of the proactive delivery-failure alert (right
    // after a send that gets rejected) is embedded directly in that send's own chat reply by the
    // backend — nothing to do here for that. Phase 2 (below) covers failures discovered any other
    // way (a delayed bounce the IMAP listener picks up later) by checking once Regi is opened.
    React.useEffect(() => {
        if (!open || loadedRef.current || !userId) return;
        loadedRef.current = true;
        setLoadingHistory(true);
        fetchApi({ method: 'GET', url: '/chat/history' })
            .then((res) => {
                const raw = Array.isArray(res.messages) ? res.messages : [];
                // Draw a divider wherever the topic changes between consecutive messages, so a past
                // "New topic" break shows as a line on reload (topic is stamped server-side).
                const withDividers = [];
                let prevTopic = null;
                raw.forEach((m) => {
                    const t = m.topic || 0;
                    if (prevTopic !== null && t !== prevTopic) withDividers.push({ role: 'divider', createdAt: m.createdAt });
                    withDividers.push(m);
                    prevTopic = t;
                });
                setMessages(withDividers);
            })
            .catch((err) => { console.error('[Regi] Failed to load chat history:', err); })
            .finally(() => { setLoadingHistory(false); setHistoryReadyTick((t) => t + 1); });
    }, [open, userId]);

    // On open (after history loads, which would otherwise wipe it): bring back the confirmation card
    // for a draft that is still pending, e.g. after a page reload.
    React.useEffect(() => {
        if (!open || !userId || historyReadyTick === 0) return;
        restoreDraftCard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, userId, historyReadyTick]);

    // Phase 2: on open, show any delivery failures not yet surfaced to this user (backend dedup
    // via /chat/alerts/new + /chat/alerts/ack — see getNewDeliveryFailures/ackDeliveryFailures).
    // message is pre-formatted server-side (formatDeliveryAlertBubble) so Phase 1 and Phase 2
    // bubbles always look identical — gated on historyReadyTick (not loadingHistory) so this never
    // runs before the history load that would otherwise wipe out its appended bubble.
    // Also re-checked while the panel is open: when a send batch drains (the Suppliers page fires
    // regi-check-delivery-failures, which nothing listened to) and when the server reports a
    // background change such as a bounce (suppliers-imported).
    const [alertCheckTick, setAlertCheckTick] = React.useState(0);
    const alertCheckBusyRef = React.useRef(false);
    React.useEffect(() => {
        const bump = () => setAlertCheckTick((t) => t + 1);
        window.addEventListener('regi-check-delivery-failures', bump);
        window.addEventListener('suppliers-imported', bump);
        return () => {
            window.removeEventListener('regi-check-delivery-failures', bump);
            window.removeEventListener('suppliers-imported', bump);
        };
    }, []);
    React.useEffect(() => {
        if (!open || !userId || historyReadyTick === 0 || alertCheckBusyRef.current) return;
        alertCheckBusyRef.current = true; // overlapping checks could show the same alert twice before the ack
        (async () => {
            try {
                const res = await fetchApi({ method: 'GET', url: '/chat/alerts/new' });
                if (res && res.count > 0 && res.message) {
                    setMessages((prev) => [...prev, {
                        role: 'assistant',
                        content: res.message,
                        createdAt: new Date().toISOString(),
                    }]);
                    try { await fetchApi({ method: 'POST', url: '/chat/alerts/ack' }); } catch (e) { /* ignore */ }
                }
            } catch (e) { /* silent — proactive alert is best-effort */ }
            alertCheckBusyRef.current = false;
        })();
    }, [open, userId, historyReadyTick, alertCheckTick]);

    // Keep the view pinned to the newest message. Also fire on `open` (and loadingHistory) so that
    // RE-opening the panel lands at the bottom too: the scroll container is unmounted while closed,
    // so on reopen it remounts fresh at scrollTop=0 (oldest); without `open` in the deps this effect
    // wouldn't re-run when messages were unchanged, leaving the user staring at old history. The
    // rAF + short timeout re-pin after late layout (tall markdown tables grow scrollHeight after the
    // first paint), so we end at the true bottom rather than a stale mid-scroll position.
    React.useEffect(() => {
        if (!open) return undefined;
        const pin = () => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; };
        pin();
        const raf = requestAnimationFrame(pin);
        const t = setTimeout(pin, 60);
        return () => { cancelAnimationFrame(raf); clearTimeout(t); };
    }, [messages, sending, open, loadingHistory]);

    // Typing dots keyframes
    React.useLayoutEffect(() => {
        const ID = 'regi-typing-dots-style';
        if (document.getElementById(ID)) return undefined;
        const el = document.createElement('style');
        el.id = ID;
        el.textContent = '@keyframes regi-dot-bounce{0%,80%,100%{transform:translateY(0);opacity:0.6}40%{transform:translateY(-5px);opacity:1}}';
        document.head.appendChild(el);
        return undefined;
    }, []);

    const dotStyle = (delay) => ({
        display: 'inline-block', width: 9, height: 9, minWidth: 9, minHeight: 9, flexShrink: 0,
        background: '#4a90e2', borderRadius: '50%',
        animationName: 'regi-dot-bounce', animationDuration: '1.2s',
        animationIterationCount: 'infinite', animationTimingFunction: 'ease-in-out',
        animationFillMode: 'both', animationDelay: delay,
    });

    // ── File helpers ──────────────────────────────────────────────────────────
    // Uploaded files are base64-encoded (~1.33x inflation) and stored together in a single
    // field of the User document, which MongoDB caps at 16 MB total (including everything
    // else already on that document). 8 MB of combined raw bytes keeps the encoded total, plus
    // the AI analysis result stored alongside it, safely under that limit — a per-file cap
    // alone doesn't help since several files under it can still sum past the real ceiling.
    const REGI_MAX_FILE_BYTES = 8 * 1024 * 1024; // 8 MB per file
    const REGI_MAX_TOTAL_RAW_BYTES = 8 * 1024 * 1024; // 8 MB combined across all attached files
    const addFiles = (fileList) => {
        const all = Array.from(fileList);
        const badType = all.filter((f) => !ACCEPT_ANY.test(f.name));
        const badSize = all.filter((f) => ACCEPT_ANY.test(f.name) && f.size > REGI_MAX_FILE_BYTES);
        if (badType.length) toast.error(`Unsupported: ${badType.map((f) => f.name).join(', ')} — only .xlsx/.csv/.docx/.txt/.pdf or images (.png/.jpg/.gif/.webp)`);
        if (badSize.length) toast.error(`Too large (>${REGI_MAX_FILE_BYTES / (1024 * 1024)} MB): ${badSize.map((f) => f.name).join(', ')}`);
        let valid = all.filter((f) => ACCEPT_ANY.test(f.name) && f.size <= REGI_MAX_FILE_BYTES);
        if (!valid.length) return;
        setAttachedFiles((prev) => {
            const names = new Set(prev.map((f) => f.name));
            valid = valid.filter((f) => !names.has(f.name));
            const existingTotal = prev.reduce((sum, f) => sum + f.size, 0);
            let runningTotal = existingTotal;
            const accepted = [];
            const rejected = [];
            for (const f of valid) {
                if (runningTotal + f.size > REGI_MAX_TOTAL_RAW_BYTES) {
                    rejected.push(f.name);
                } else {
                    runningTotal += f.size;
                    accepted.push(f);
                }
            }
            if (rejected.length) {
                toast.error(`Skipped (combined upload would exceed ${REGI_MAX_TOTAL_RAW_BYTES / (1024 * 1024)} MB): ${rejected.join(', ')}`);
            }
            return [...prev, ...accepted];
        });
    };

    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
    const handleDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
    };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    // ── Send ──────────────────────────────────────────────────────────────────
    const handleSend = async () => {
        const text = input.trim();
        const hasFiles = attachedFiles.length > 0;
        if ((!text && !hasFiles) || sending) return;

        const filesToSend = [...attachedFiles];
        setInput('');
        setAttachedFiles([]);
        setSending(true);
        setProgressTools([]);
        const controller = new AbortController();
        sendAbortRef.current = controller;

        // Show the user message immediately. Images render as inline thumbnails (object URLs);
        // non-image files keep their filename chips. Thumbnails are session-only (not persisted).
        const previewImageFiles = filesToSend.filter((f) => IMAGE_EXTS.test(f.name));
        const previewDocFiles = filesToSend.filter((f) => !IMAGE_EXTS.test(f.name));
        const imagePreviewUrls = previewImageFiles.map((f) => URL.createObjectURL(f));
        const displayContent = text || '';
        const localId = `u-${Date.now()}`;
        setMessages((prev) => [...prev, {
            role: 'user',
            content: displayContent,
            _localId: localId,
            _files: previewDocFiles.length ? previewDocFiles.map((f) => f.name) : undefined,
            _imageUrls: imagePreviewUrls.length ? imagePreviewUrls : undefined,
            createdAt: new Date().toISOString(),
        }]);

        // Set once the chat request goes out / once the server has started the stream — the
        // catch block uses them to tell a Stop that beat the message there from one that didn't.
        let sentAtMs = null;
        let streamStarted = false;
        try {
            const imageFiles = filesToSend.filter((f) => IMAGE_EXTS.test(f.name));
            const importFiles = filesToSend.filter((f) => !IMAGE_EXTS.test(f.name));
            const hasImportFiles = importFiles.length > 0;
            const hasImageFiles = imageFiles.length > 0;
            let chatMessage = text;

            if (hasImportFiles) {
                // Upload files to /auth/import-assistant — this analyzes them and stores
                // the result server-side so Regi can access it via get_pending_import tool.
                const formData = new FormData();
                importFiles.forEach((f) => formData.append('files', f));
                // So the backend can resolve the same "effective owner" the chat turn's tool
                // calls use (resolveOwnerOrForbid) — without this, an admin viewing another
                // user's project would have the upload stored under their own id, and every
                // later get_pending_import/get_pending_reply_batch call (looked up under the
                // project's real owner) would find nothing.
                if (currentProjectId) formData.append('projectId', currentProjectId);
                // Pass the same abort signal as the chat/message call below — without it, Stop
                // had no effect while a file upload/analysis was still in flight; the request
                // (and its real Claude API call) ran to completion server-side regardless.
                await fetchApi({ method: 'POST', url: endpoints.importAssistant, data: formData, signal: controller.signal });

                const fileNames = importFiles.map((f) => f.name).join(', ');
                chatMessage = text
                    ? `[Files uploaded: ${fileNames}] ${text}`
                    : `[Files uploaded: ${fileNames}] Please check the pending import, tell me what you found, and help me import the data.`;
            }

            // Images ride the chat turn itself (multipart) so the multimodal model sees them;
            // non-image files went to the import pipeline above.
            let chatReqData;
            if (hasImageFiles) {
                const fd = new FormData();
                fd.append('message', chatMessage || '');
                if (currentProjectId) fd.append('currentProjectId', currentProjectId);
                if (regiPageContext) fd.append('pageContext', JSON.stringify(regiPageContext));
                imageFiles.forEach((f) => fd.append('images', f));
                chatReqData = fd;
            } else {
                chatReqData = { message: chatMessage, currentProjectId, pageContext: regiPageContext || undefined };
            }

            // /chat/message is SSE now (2026-10-10) — a turn can chain several tool calls (e.g.
            // list emails -> read one -> read an attachment), each a real Claude round trip
            // minutes long in total, and the backend streams heartbeats for exactly that reason.
            // Bypasses fetchApi (axios) deliberately: the browser's streaming-body reader needs a
            // raw fetch. This loses fetchApi's silent 401-refresh-and-retry, which only matters
            // for the rare case of the access token expiring in the instant the request is sent
            // (a token expiring mid-stream doesn't matter — the status code is fixed at the top).
            sentAtMs = Date.now();
            const accessToken = await getAccessToken();
            const sseResp = await fetch(`${config[config.env].httpURL}/chat/message`, {
                method: 'POST',
                headers: {
                    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
                    ...(hasImageFiles ? {} : { 'Content-Type': 'application/json' }),
                },
                body: hasImageFiles ? chatReqData : JSON.stringify(chatReqData),
                signal: controller.signal,
            });

            const contentType = sseResp.headers.get('content-type') || '';
            let res;
            if (!sseResp.ok || !contentType.includes('text/event-stream')) {
                // Pre-stream failure (400/401/429/500/...) — plain JSON body, same shape the old
                // axios error path already expected, so the catch block below needs no changes.
                let errBody = null;
                try { errBody = await sseResp.json(); } catch (e) { /* no JSON body */ }
                const err = new Error((errBody && errBody.message) || `Request failed (${sseResp.status})`);
                err.code = sseResp.status;
                throw err;
            }
            streamStarted = true;

            const { donePayload, errorPayload } = await parseSseBody(sseResp.body, (p) => setProgressTools((p && p.tools) || []));
            if (errorPayload) {
                throw new Error(errorPayload.message || 'Something went wrong.');
            }
            res = donePayload;
            if (!res) {
                // Stream ended with neither event — e.g. a proxy or network hiccup mid-turn.
                res = await pollForSavedReply(sentAtMs);
                if (!res) {
                    // The message did reach Regi (the stream had started), so keep it and keep waiting
                    // in the background — dropping it back into the input invited a re-send that ran
                    // the same actions twice.
                    const waitId = `wait-${localId}`;
                    setMessages((prev) => [...prev, {
                        role: 'assistant', content: NOTE_CONNECTION_DROPPED, _auto: true, _stopId: waitId, createdAt: new Date().toISOString(),
                    }]);
                    pollForSavedReply(sentAtMs, { attempts: 40 }).then((saved) => {
                        if (!saved) {
                            loadedRef.current = false; // so reopening Regi reloads history with the reply
                            setMessages((prev) => prev.map((m) => (m._stopId === waitId ? { ...m, content: STOP_NOTE_STILL_WORKING } : m)));
                            return;
                        }
                        setMessages((prev) => prev.map((m) => (m._stopId === waitId ? { ...m, content: saved.reply || '', _auto: false } : m)));
                        restoreDraftCard();
                        refreshAfterWrites(saved.writeToolsExecuted);
                    });
                    return;
                }
            }

            setMessages((prev) => [...prev, {
                role: 'assistant',
                content: res.reply || '',
                createdAt: new Date().toISOString(),
            }]);

            // Confirmation card: rendered from the backend's staged draft (the exact final email),
            // never from the model's own text. Then retire cards the backend no longer holds.
            if ('pendingDraftId' in res) {
                if (res.stagedDraft) addDraftCard(res.stagedDraft);
                const sentByText = !!res.deterministic && (res.writeToolsExecuted || []).includes('send_custom_email');
                retireDraftCards(res.pendingDraftId, sentByText);
            } else {
                restoreDraftCard(); // reply recovered by polling after a dropped stream
            }

            // Trigger page refreshes when Regi executed a write tool.
            await refreshAfterWrites(res.writeToolsExecuted);
        } catch (err) {
            if (controller.signal.aborted && sentAtMs) {
                // Stop never cancels anything server-side: a message that reached the backend is
                // processed and saved regardless (including side effects like clearing a staged
                // draft). So keep it on screen and show the reply once it lands — only "un-send"
                // (restore the draft) when it verifiably never got there.
                const stopId = `stop-${localId}`;
                const setNote = (content, isReply) => setMessages((prev) => prev.map((m) => (
                    m._stopId === stopId ? { ...m, content, _auto: !isReply } : m
                )));
                setMessages((prev) => [...prev, {
                    role: 'assistant',
                    content: streamStarted ? STOP_NOTE_PROCESSING : STOP_NOTE_CHECKING,
                    _auto: true,
                    _stopId: stopId,
                    createdAt: new Date().toISOString(),
                }]);
                if (streamStarted) {
                    // Definitely received — don't block further chatting while waiting for it.
                    pollForSavedReply(sentAtMs, { attempts: 40 }).then((saved) => {
                        if (!saved) {
                            loadedRef.current = false; // so reopening Regi reloads history with the reply
                            setNote(STOP_NOTE_STILL_WORKING, false);
                            return;
                        }
                        setNote(saved.reply || '', true);
                        restoreDraftCard(); // the stopped turn may have staged a draft
                        refreshAfterWrites(saved.writeToolsExecuted);
                    });
                } else {
                    // Stopped before the server confirmed receipt. A short check catches the paths
                    // that only open the stream after doing their work (e.g. a confirmed send that
                    // already went out); sending stays blocked meanwhile so a resend can't race it.
                    const saved = await pollForSavedReply(sentAtMs, { attempts: 3, intervalMs: 2000 });
                    if (saved) {
                        setNote(saved.reply || '', true);
                        restoreDraftCard();
                        refreshAfterWrites(saved.writeToolsExecuted);
                    } else {
                        setMessages((prev) => prev.filter((m) => m._stopId !== stopId && m._localId !== localId));
                        setInput((cur) => cur || text);
                        if (hasFiles) setAttachedFiles((cur) => (cur.length ? cur : filesToSend));
                    }
                }
            } else {
                // A genuine error, or a Stop before the chat request was even sent (e.g. during a
                // file upload): drop the optimistic message and restore the draft for a retry.
                if (!controller.signal.aborted) {
                    const msg = (err && (err.message || err.msg)) || 'Something went wrong.';
                    toast.error(`Regi: ${msg}`);
                }
                setMessages((prev) => prev.slice(0, -1));
                setInput(text);
                if (hasFiles) setAttachedFiles(filesToSend);
            }
        } finally {
            setSending(false);
            setProgressTools([]);
        }
    };

    const handleStopSend = () => { sendAbortRef.current?.abort(); };

    const handleNewTopic = async () => {
        try {
            await fetchApi({ method: 'POST', url: '/chat/new-topic' });
            setMessages((prev) => [...prev, { role: 'divider', createdAt: new Date().toISOString() }]);
            retireDraftCards(null); // the server dropped the staged draft with the old topic
            toast.success('Started a new topic — Regi will ignore earlier context.');
        } catch (_) {
            toast.error('Failed to start a new topic.');
        }
    };

    const handleClear = async () => {
        if (!window.confirm('Clear the entire conversation with Regi?')) return;
        try { await fetchApi({ method: 'DELETE', url: '/chat/history' }); setMessages([]); }
        catch (_) { toast.error('Failed to clear chat.'); }
    };

    const handleKey = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    };

    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        const files = [];
        for (const item of items) {
            if (item.kind === 'file') {
                let f = item.getAsFile();
                if (!f) continue;
                // Pasted screenshots often arrive with no filename — synthesize one from the MIME
                // type so the extension check below (and the backend) recognize it as an image.
                if ((!f.name || !ACCEPT_ANY.test(f.name)) && /^image\//.test(f.type || '')) {
                    const ext = (f.type.split('/')[1] || 'png').toLowerCase();
                    f = new File([f], `pasted-image-${Date.now()}.${ext}`, { type: f.type });
                }
                if (ACCEPT_ANY.test(f.name)) files.push(f);
            }
        }
        if (files.length) { e.preventDefault(); addFiles(files); }
    };

    if (hidden) return null;

    return (
        <>
            {/* ── Floating bubble ── */}
            {!open && (
                <Tooltip title="Chat with Regi (drag to move)" arrow placement="left">
                    <IconButton
                        ref={bubbleRef}
                        onPointerDown={(e) => bubbleDrag.startDrag(e, () => bubbleRef.current?.getBoundingClientRect())}
                        onClick={() => { if (bubbleDrag.wasDragged()) return; setOpen(true); }}
                        style={{
                            position: 'fixed',
                            ...(bubbleDrag.pos ? { left: bubbleDrag.pos.left, top: bubbleDrag.pos.top } : { bottom: 20, right: 20 }),
                            zIndex: 2000, width: 56, height: 56,
                            background: '#4a90e2', color: '#fff',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.2)', cursor: 'grab', touchAction: 'none',
                        }}
                    >
                        <ChatBubbleOutlineIcon />
                    </IconButton>
                </Tooltip>
            )}

            {/* ── Chat window ── */}
            {open && (
                <div
                    ref={windowRef}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                        position: 'fixed',
                        ...(windowDrag.pos ? { left: windowDrag.pos.left, top: windowDrag.pos.top } : { bottom: 20, right: 20 }),
                        width: 560, height: 600,
                        background: '#fff', borderRadius: 12,
                        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
                        zIndex: 2000, display: 'flex', flexDirection: 'column',
                        overflow: 'hidden',
                        border: isDragging ? '2px solid #4a90e2' : '1px solid #e3e8ee',
                        transition: 'border 0.1s',
                    }}
                >
                    {/* Drag-file overlay */}
                    {isDragging && (
                        <div style={{
                            position: 'absolute', inset: 0, zIndex: 20,
                            background: 'rgba(74,144,226,0.08)',
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', gap: 8,
                            pointerEvents: 'none',
                        }}>
                            <CloudUploadIcon style={{ fontSize: 44, color: '#4a90e2' }} />
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#4a90e2' }}>Drop to analyze</div>
                            <div style={{ fontSize: 11, color: '#8895a2' }}>.xlsx · .csv · .docx · .txt · .pdf</div>
                        </div>
                    )}

                    {/* ── Header ── */}
                    <div
                        onPointerDown={(e) => {
                            if (e.target.closest('button')) return;
                            windowDrag.startDrag(e, () => windowRef.current?.getBoundingClientRect());
                        }}
                        style={{
                            background: '#4a90e2', color: '#fff', padding: '10px 14px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            flexShrink: 0, cursor: 'grab', touchAction: 'none', userSelect: 'none',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <ChatBubbleOutlineIcon style={{ fontSize: 20 }} />
                            <div>
                                <div style={{ fontWeight: 600, fontSize: 15 }}>Regi</div>
                                <div style={{ fontSize: 11, opacity: 0.8 }}>Compliance assistant</div>
                            </div>
                        </div>
                        <div>
                            <Tooltip title="New topic — fresh context, keeps history" arrow PopperProps={{ style: { zIndex: 2100 } }}>
                                <IconButton size="small" onClick={handleNewTopic} style={{ color: '#fff' }}>
                                    <AddCommentOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Clear chat" arrow PopperProps={{ style: { zIndex: 2100 } }}>
                                <IconButton size="small" onClick={handleClear} style={{ color: '#fff' }}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Minimize" arrow PopperProps={{ style: { zIndex: 2100 } }}>
                                <IconButton size="small" onClick={() => setOpen(false)} style={{ color: '#fff' }}>
                                    <MinimizeIcon fontSize="small" style={{ marginTop: -6 }} />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </div>

                    {/* ── Message list ── */}
                    <div
                        ref={scrollRef}
                        style={{
                            flex: 1, overflowY: 'auto', padding: 12,
                            background: '#f7f9fc',
                            display: 'flex', flexDirection: 'column', gap: 10,
                        }}
                    >
                        {loadingHistory && (
                            <div style={{ textAlign: 'center', color: '#8895a2', fontSize: 12 }}>Loading…</div>
                        )}

                        {/* Empty state — suggestions */}
                        {!loadingHistory && messages.length === 0 && (
                            <div>
                                <div style={{ fontSize: 13, color: '#5a6b7a', marginBottom: 10 }}>
                                    Hi! I&apos;m Regi. Ask me anything, or drop a file here (.xlsx, .csv, .docx, .txt, .pdf).
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {SUGGESTIONS.map((s) => (
                                        <div
                                            key={s.text}
                                            onClick={() => setInput(s.text)}
                                            style={{
                                                background: '#fff', border: '1px solid #d8e1ea',
                                                borderRadius: 8, padding: '8px 12px', fontSize: 12,
                                                color: '#2c3e50', cursor: 'pointer',
                                            }}
                                        >
                                            {s.text}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Messages */}
                        <style>{`
                            .regi-md table { border-collapse: collapse; margin: 6px 0; font-size: inherit; }
                            .regi-md th, .regi-md td { border: 1px solid #d8e1ea; padding: 4px 8px; text-align: left; }
                            .regi-md th { background: #f4f7fa; font-weight: 600; }
                        `}</style>
                        {messages.map((m, i) => (
                            m._draftCard ? (
                                <DraftCard key={i} card={m._draftCard} onAct={actOnDraft} busy={sending || m._draftCard.status === 'sending'} />
                            ) : m.role === 'divider' ? (
                                <div key={i} style={{
                                    alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 8,
                                    width: '100%', margin: '2px 0', color: '#8895a2', fontSize: 11, userSelect: 'none',
                                }}>
                                    <span style={{ flex: 1, height: 1, background: '#dce3ea' }} />
                                    新话题 / New topic
                                    <span style={{ flex: 1, height: 1, background: '#dce3ea' }} />
                                </div>
                            ) : (
                            <div key={i} style={{
                                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                                maxWidth: '88%',
                                background: m._auto ? 'rgba(74,144,226,0.08)' : m.role === 'user' ? '#4a90e2' : '#fff',
                                color: m._auto ? '#5a6b7a' : m.role === 'user' ? '#fff' : '#1a2733',
                                padding: '8px 12px', borderRadius: 10,
                                fontSize: m._auto ? 11 : 13, lineHeight: 1.5,
                                wordBreak: 'break-word',
                                border: m._auto ? '1px dashed #b0c4d8' : m.role === 'assistant' ? '1px solid #e3e8ee' : 'none',
                                boxShadow: m.role === 'assistant' && !m._auto ? '0 1px 3px rgba(0,0,0,0.04)' : 'none',
                                fontStyle: m._auto ? 'italic' : 'normal',
                            }}>
                                {m._imageUrls && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: m.content ? 6 : 0 }}>
                                        {m._imageUrls.map((url, idx) => (
                                            <img
                                                key={idx}
                                                src={url}
                                                alt="image"
                                                onClick={() => window.open(url, '_blank')}
                                                style={{ maxWidth: 160, maxHeight: 160, borderRadius: 8, cursor: 'pointer', display: 'block', border: '1px solid rgba(255,255,255,0.4)' }}
                                            />
                                        ))}
                                    </div>
                                )}
                                {m._files && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
                                        {m._files.map((name) => (
                                            <span key={name} style={{
                                                background: 'rgba(255,255,255,0.25)',
                                                borderRadius: 4, padding: '1px 6px',
                                                fontSize: 10, border: '1px solid rgba(255,255,255,0.4)',
                                            }}>
                                                📎 {name}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {m.role === 'assistant' ? (
                                    <div
                                        className="regi-md"
                                        dangerouslySetInnerHTML={{ __html: sanitizeAiHtml(marked.parse(m.content || '', { breaks: true })) }}
                                    />
                                ) : (
                                    <span style={{ whiteSpace: 'pre-wrap' }}>{m.content}</span>
                                )}
                            </div>
                            )
                        ))}

                        {/* Typing indicator */}
                        {sending && (
                            <div style={{
                                alignSelf: 'flex-start', background: '#fff',
                                padding: '10px 14px', borderRadius: 10,
                                border: '1px solid #e3e8ee',
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                            }}>
                                <span style={dotStyle('-0.32s')} />
                                <span style={dotStyle('-0.16s')} />
                                <span style={dotStyle('0s')} />
                                {progressTools.length > 0 && (
                                    <span style={{ marginLeft: 8, fontSize: 12, color: '#5a6b7a' }}>
                                        {progressLabel(progressTools)}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── Suggestions panel ── */}
                    {showSuggestions && (
                        <div style={{
                            flexShrink: 0, borderTop: '1px solid #e3e8ee',
                            background: '#f7f9fc', padding: '8px 10px',
                            display: 'flex', flexDirection: 'column', gap: 5,
                        }}>
                            {SUGGESTIONS.map((s) => (
                                <div
                                    key={s.text}
                                    onClick={() => { setShowSuggestions(false); setInput(s.text); }}
                                    style={{
                                        background: '#fff', border: '1px solid #d8e1ea',
                                        borderRadius: 7, padding: '6px 10px', fontSize: 12,
                                        color: '#2c3e50', cursor: 'pointer',
                                    }}
                                >
                                    {s.text}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ── Attached file chips ── */}
                    {attachedFiles.length > 0 && (
                        <div style={{
                            flexShrink: 0, padding: '5px 10px 2px',
                            borderTop: '1px solid #e3e8ee', background: '#fff',
                            display: 'flex', flexWrap: 'wrap', gap: 4,
                        }}>
                            {attachedFiles.map((f) => (
                                <div key={f.name} style={{
                                    background: '#f0f4ff', border: '1px solid #bdd9f5',
                                    borderRadius: 12, padding: '2px 6px 2px 8px',
                                    fontSize: 11, color: '#2c3e50',
                                    display: 'flex', alignItems: 'center', gap: 3,
                                    maxWidth: 200,
                                }}>
                                    <span style={{
                                        overflow: 'hidden', textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        📎 {f.name}
                                    </span>
                                    <CloseIcon
                                        style={{ fontSize: 12, cursor: 'pointer', color: '#8895a2', flexShrink: 0 }}
                                        onClick={() => setAttachedFiles((prev) => prev.filter((x) => x.name !== f.name))}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ── Input bar ── */}
                    <div style={{
                        padding: 10, borderTop: '1px solid #e3e8ee', background: '#fff',
                        display: 'flex', gap: 6, flexShrink: 0, alignItems: 'flex-end',
                    }}>
                        <Tooltip title={showSuggestions ? 'Hide suggestions' : 'Show suggestions'} arrow placement="top" PopperProps={{ style: { zIndex: 2100 } }}>
                            <IconButton
                                size="small"
                                onClick={() => setShowSuggestions((v) => !v)}
                                style={{ color: showSuggestions ? '#4a90e2' : '#a0aab3', padding: 4, marginBottom: 2 }}
                            >
                                <LightbulbOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Attach file (.xlsx, .csv, .docx, .txt, .pdf)" arrow placement="top" PopperProps={{ style: { zIndex: 2100 } }}>
                            <IconButton
                                size="small"
                                onClick={() => fileInputRef.current?.click()}
                                style={{ color: attachedFiles.length ? '#4a90e2' : '#a0aab3', padding: 4, marginBottom: 2 }}
                            >
                                <AttachFileIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept=".xlsx,.xls,.csv,.docx,.doc,.txt,.pdf,.png,.jpg,.jpeg,.gif,.webp,image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                        />

                        <textarea
                            placeholder={
                                attachedFiles.length
                                    ? 'Add a note (optional) then send…'
                                    : sending
                                        ? 'Regi is thinking… click ■ to stop'
                                        : 'Ask Regi anything, or drop a file here…'
                            }
                            value={input}
                            onChange={(e) => !sending && setInput(e.target.value)}
                            onKeyDown={handleKey}
                            onPaste={handlePaste}
                            rows={2}
                            style={{
                                flex: 1, resize: 'none',
                                border: '1px solid #d8e1ea', borderRadius: 8,
                                padding: '8px 10px', fontSize: 13,
                                fontFamily: 'inherit', outline: 'none',
                            }}
                        />

                        <IconButton
                            onClick={sending ? handleStopSend : handleSend}
                            disabled={!sending && !input.trim() && !attachedFiles.length}
                            style={{
                                background: sending
                                    ? '#e53935'
                                    : (input.trim() || attachedFiles.length ? '#4a90e2' : '#d8e1ea'),
                                color: '#fff', width: 40, height: 40,
                            }}
                        >
                            {sending ? <StopIcon fontSize="small" /> : <SendIcon fontSize="small" />}
                        </IconButton>
                    </div>
                </div>
            )}
        </>
    );
};

export default Regi;
